import React, { useState, useMemo } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import DarkModeSwitch from "../components/DarkModeSwitch";
import Logo from "../assets/images/logo.png";
import WalletButton from "../components/WalletButton";
import { useWallet } from "@solana/wallet-adapter-react";
import { useAtomValue } from "jotai";
import useGetReferralStatus from "../hooks/useGetReferralStatus";
import { walletPublicKeyAtom } from "../atoms/wallet";

const MainLayout = () => {
  const location = useLocation();
  const { connected } = useWallet();
  const walletPublicKey = useAtomValue(walletPublicKeyAtom);

  const { data: referralData } = useGetReferralStatus(walletPublicKey, {
    enabled: Boolean(connected && walletPublicKey),
  });

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDropDownOpen, setIsDropDownOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(document.documentElement.classList.contains("dark"));
  const [isMinimized, setIsMinimized] = useState(false);

  const isActive = (path) => location.pathname === path;

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const toggleDropdown = () => {
    setIsDropDownOpen(!isDropDownOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  React.useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          setIsDarkMode(document.documentElement.classList.contains("dark"));
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const navigation = useMemo(() => {
    const baseNavigation = [
      {
        name: "Ninja Terminal",
        href: "/go/terminal",
        icon: (
          <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v15a1 1 0 0 0 1 1h15M8 16l2.5-5.5 3 3L17.273 7 20 9.667" />
          </svg>
        ),
      },
      {
        name: "Ninja Volume Army",
        href: "/go",
        icon: (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 -64 640 640" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
            <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
            <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
            <g id="SVGRepo_iconCarrier">
              <path d="M312 8C175 8 64 119 64 256s111 248 248 248 248-111 248-248c0-25.38-3.82-49.86-10.9-72.91l63.92 52.97L640 173.49l-98.2-10.89c-.65-1.6-1.31-3.19-2-4.77l94.89-40.43L595 61.64l-60.41 84.91C494.17 64.46 409.7 8 312 8zM191.99 256c0-9.3 4.1-17.5 10.5-23.4l-31-9.3c-8.5-2.5-13.3-11.5-10.7-19.9 2.5-8.5 11.4-13.2 19.9-10.7l80 24c8.5 2.5 13.3 11.5 10.7 19.9-2.1 6.9-8.4 11.4-15.3 11.4-.5 0-1.1-.2-1.7-.2.7 2.7 1.7 5.3 1.7 8.2 0 17.7-14.3 32-32 32s-32.1-14.3-32.1-32zm252.61-32.7l-31 9.3c6.3 5.8 10.5 14.1 10.5 23.4 0 17.7-14.3 32-32 32s-32-14.3-32-32c0-2.9.9-5.6 1.7-8.2-.6.1-1.1.2-1.7.2-6.9 0-13.2-4.5-15.3-11.4-2.5-8.5 2.3-17.4 10.7-19.9l80-24c8.4-2.5 17.4 2.3 19.9 10.7 2.5 8.5-2.3 17.4-10.8 19.9zm-265.19-52.28h262.56c42.35 0 54.97 49.74 53.8 83.99-1.18 34.83-41.79 72.53-72.23 72.53-61.58 0-73.62-40.25-112.85-40.28-39.23.03-51.27 40.28-112.85 40.28-30.44 0-71.05-37.7-72.23-72.53-1.17-34.25 11.45-83.99 53.8-83.99z"></path>
            </g>
          </svg>
        ),
      },
      {
        name: "Ninja Stealth Buy",
        href: "/go/stealth-buy",
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
            <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
            <g id="SVGRepo_iconCarrier">
              {" "}
              <path d="M21 17.5C21 19.433 19.433 21 17.5 21C15.567 21 14 19.433 14 17.5C14 15.567 15.567 14 17.5 14C19.433 14 21 15.567 21 17.5Z" stroke="currentColor" strokeWidth="1.5"></path>{" "}
              <path d="M2 11H14M22 11H18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path>{" "}
              <path
                d="M4 11L4.6138 8.54479C5.15947 6.36211 5.43231 5.27077 6.24609 4.63538C7.0057 4.0423 8.03639 4.00282 10 4.00019M20 11L19.3862 8.54479C18.8405 6.36211 18.5677 5.27077 17.7539 4.63538C16.9943 4.0423 15.9636 4.00282 14 4.00019"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              ></path>{" "}
              <path d="M10 17.5C10 19.433 8.433 21 6.5 21C4.567 21 3 19.433 3 17.5C3 15.567 4.567 14 6.5 14C8.433 14 10 15.567 10 17.5Z" stroke="currentColor" strokeWidth="1.5"></path>{" "}
              <path d="M10 17.4999L10.6584 17.1707C11.5029 16.7484 12.4971 16.7484 13.3416 17.1707L14 17.4999" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path>{" "}
            </g>
          </svg>
        ),
      },
      {
        name: "Ninja Flash Buy",
        href: "/go/flash-buy",
        icon: (
          <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        ),
      },
    ];

    // if (connected) {
    //   baseNavigation.push({
    //     name: "Terminal",
    //     href: "/go/terminal",
    //     icon: (

    //     ),
    //   });
    // }

    if (connected) {
      baseNavigation.push({
        name: "Dashboard",
        href: "/go/dashboard",
        icon: (
          <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 18">
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6.143 1H1.857A.857.857 0 0 0 1 1.857v4.286c0 .473.384.857.857.857h4.286A.857.857 0 0 0 7 6.143V1.857A.857.857 0 0 0 6.143 1Zm10 0h-4.286a.857.857 0 0 0-.857.857v4.286c0 .473.384.857.857.857h4.286A.857.857 0 0 0 17 6.143V1.857A.857.857 0 0 0 16.143 1Zm-10 10H1.857a.857.857 0 0 0-.857.857v4.286c0 .473.384.857.857.857h4.286A.857.857 0 0 0 7 16.143v-4.286A.857.857 0 0 0 6.143 11Zm10 0h-4.286a.857.857 0 0 0-.857.857v4.286c0 .473.384.857.857.857h4.286a.857.857 0 0 0 .857-.857v-4.286a.857.857 0 0 0-.857-.857Z"
            />
          </svg>
        ),
      });

      if (referralData?.active === true) {
        baseNavigation.push({
          name: "Referral",
          href: "/go/referral",
          icon: (
            <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 18">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M14 3a3 3 0 1 1-1.614 5.53M15 12a4 4 0 0 1 4 4v1h-3.348M10 4.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0ZM5 11h3a4 4 0 0 1 4 4v2H1v-2a4 4 0 0 1 4-4Z"
              />
            </svg>
          ),
        });
      }
    }

    return baseNavigation;
  }, [connected, referralData?.active]);

  return (
    <div className="main-layout-container">
      <nav className="fixed top-0 z-50 w-full bg-primary border-b border-gray-200 dark:bg-secondary dark:border-secondary">
        <div className="px-3 py-3 lg:px-5 lg:pl-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center justify-start rtl:justify-end">
              <button
                onClick={toggleSidebar}
                type="button"
                className="inline-flex items-center p-2 text-sm text-gray-500 rounded-lg sm:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600"
              >
                <svg className="w-6 h-6" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    clipRule="evenodd"
                    fillRule="evenodd"
                    d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
                  ></path>
                </svg>
              </button>
              <button
                onClick={toggleMinimize}
                type="button"
                className="hidden sm:inline-flex items-center p-2 text-sm text-gray-500 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600"
              >
                <svg className="w-6 h-6" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    clipRule="evenodd"
                    fillRule="evenodd"
                    d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
                  ></path>
                </svg>
              </button>
              <Link to="/" className="flex ms-2 md:me-24 items-center">
                <img src={Logo} className="h-8 me-3" alt="Ninja Pump Logo" />
                <div className="flex md:items-center flex-col md:flex-row">
                  <span className="self-center text-xl text-secondary dark:text-primary font-title sm:text-2xl whitespace-nowrap">NinjaPump</span>
                  <span className="md:ml-1 text-[10px] md:text-xs text-secondary/70 dark:text-primary/70 font-medium bg-secondary/10 dark:bg-primary/10 px-1.5 py-0.5 rounded-md w-fit">beta</span>
                </div>
              </Link>
            </div>

            <div className="flex align-middle gap-2 justify-center">
              <WalletButton />
            </div>
          </div>
        </div>
      </nav>

      <aside
        id="logo-sidebar"
        className={`fixed top-0 left-0 z-40 ${isMinimized ? "w-16" : "w-64"} h-screen pt-20 transition-all duration-300 
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} 
          bg-primary border-r border-gray-200 sm:translate-x-0 dark:bg-secondary dark:border-gray-700`}
        aria-label="Sidebar"
      >
        <div className="flex flex-col h-full px-3 pb-4 overflow-y-auto bg-primary dark:bg-secondary">
          <ul className="space-y-2 font-medium">
            {navigation
              .filter((item) => !item.external)
              .map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    onClick={closeSidebar}
                    className={`flex items-center p-2 rounded-lg group ${
                      isActive(item.href) ? "bg-secondary dark:bg-primary text-primary dark:text-secondary" : "text-secondary hover:bg-secondary hover:text-primary dark:hover:text-secondary dark:text-white dark:hover:bg-primary"
                    }`}
                  >
                    <div className="flex items-center justify-center h-6 w-6">{item.icon}</div>
                    {!isMinimized && <span className="flex-1 ms-3 whitespace-nowrap">{item.name}</span>}
                  </Link>
                </li>
              ))}
          </ul>

          {/* Bottom Section - only show when not minimized */}
          {!isMinimized && (
            <div className="mt-auto">
              {/* Customer Care Link */}
              <div className="mb-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                <a
                  href="https://t.me/+CqH9D8RMSkU3ZWE1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-2 rounded-lg group text-secondary hover:bg-secondary hover:text-primary dark:hover:text-secondary dark:text-white dark:hover:bg-primary"
                >
                  <div className="flex items-center justify-center h-6 w-6">
                    <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8-4-4H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-3l-4 4Z" />
                    </svg>
                  </div>
                  <span className="flex-1 ms-3 whitespace-nowrap">Ninja Customer Care</span>
                </a>
              </div>

              {/* Documentation Link */}
              <div className="mb-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                <a
                  href="https://ninja-pump.gitbook.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-2 rounded-lg group text-secondary hover:bg-secondary hover:text-primary dark:hover:text-secondary dark:text-white dark:hover:bg-primary"
                >
                  <div className="flex items-center justify-center h-6 w-6">
                    <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25"
                      />
                    </svg>
                  </div>
                  <span className="flex-1 ms-3 whitespace-nowrap">Docs</span>
                </a>
              </div>

              {/* Dark Mode Switch */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex flex-col items-center p-2 rounded-lg">
                  <span className="text-secondary dark:text-primary mb-2">{isDarkMode ? "Switch to Light" : "Switch to Dark"}</span>
                  <DarkModeSwitch />
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>

      <div className={`p-4 ${isMinimized ? "sm:ml-16" : "sm:ml-64"} transition-all duration-300 bg-neutral dark:bg-primary`}>
        <div className="p-4 rounded-lg mt-14 min-h-screen">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default MainLayout;
